import {
  createFileRoute,
  useParams,
  Outlet,
  useLocation,
} from "@tanstack/react-router"
import { checkPermissions } from "@/utils/checkPermissions"
import {
  LayoutWithSidebar,
  DetailSidebar,
  Separator,
  useLayoutConfig,
  projectRoutesMap,
  formatLearningPathNames,
  getTeacherNames,
  getGroupProjectNames,
} from "toptaal-shared"
import { DetailInfoList } from "toptaal-shared"
import { Suspense } from "react"
import { useGroupSuspenseQuery } from "@/api/queries/groups/useGroupSuspenseQuery"
import ScreenWrapper from "@/components/ScreenWrapper"

export const Route = createFileRoute("/beheer/groepen/$groupId")({
  component: Parent,
  beforeLoad: () => checkPermissions("read", "groups"),
})

function Parent() {
  const layoutConfig = useLayoutConfig()
  const { pathname } = useLocation()

  const { groupId } = useParams({
    from: "/beheer/groepen/$groupId",
  })

  const base = `/beheer/groepen/${groupId}`

  return (
    <ScreenWrapper className="!px-0">
      <Suspense>
        <LayoutWithSidebar
          rightPanelClassName={layoutConfig.rightPanelClassName}
          disableRightPanelSuspense={layoutConfig.disableRightPanelSuspense}
          sidebar={<GroupSidebar base={base} pathname={pathname} />}
        >
          <Outlet />
        </LayoutWithSidebar>
      </Suspense>
    </ScreenWrapper>
  )
}

function GroupSidebar({ base, pathname }: { base: string; pathname: string }) {
  const { groupId } = useParams({
    from: "/beheer/groepen/$groupId",
  })
  const { data } = useGroupSuspenseQuery({ groupId: Number(groupId) })

  const sidebarNavigationItems = [
    {
      label: "Gegevens",
      to: `${base}/gegevens`,
    },
    {
      label: "Cursisten",
      to: `${base}/students`,
    },
    {
      label: "Correspondentie",
      to: `${base}/correspondentie`,
    },
    {
      label: "Interne Notities",
      to: `${base}/notities`,
    },
    {
      label: "Documenten",
      to: `${base}/documenten`,
    },
    {
      label: "Lessen",
      to: `${base}/lessen`,
    },
    {
      label: "Toetsoverzicht",
      to: `${base}/toets-overzicht`,
    },
    {
      label: "Presentieoverzicht",
      to: `${base}/presentie-overzicht`,
    },
    {
      label: "Lesmaterialen",
      to: `${base}/lesmaterialen`,
    },
    ...(!data.data.legacyMtt1Id
      ? [
          {
            label: "Rooster",
            to: `${base}/rooster`,
          },
        ]
      : []),
  ]

  return (
    <DetailSidebar
      items={sidebarNavigationItems}
      isItemActive={(route: string) => pathname.startsWith(route)}
      goBackLabel="Groepen"
      goBackRoute="/beheer/groepen"
    >
      <div className="rounded px-4 py-5  bg-white">
        <GroupHeader />

        <Separator className="my-4" />

        <GroupDetails />
      </div>
      <Separator className="my-4" />
    </DetailSidebar>
  )
}

function GroupHeader() {
  const { groupId } = useParams({
    from: "/beheer/groepen/$groupId",
  })
  const { data: group } = useGroupSuspenseQuery({
    groupId: Number(groupId),
  })

  return <h3 className="font-bold">{group.data.id}</h3>
}

function GroupDetails() {
  const { groupId } = useParams({
    from: "/beheer/groepen/$groupId",
  })
  const { data } = useGroupSuspenseQuery({
    groupId: Number(groupId),
  })

  const group = data.data

  return (
    <DetailInfoList
      items={[
        {
          label: "Contract",
          value: getGroupProjectNames(group.projects),
        },
        {
          label: "Leerlijn",

          value: formatLearningPathNames({
            learningPaths: group.learningPaths,
            moduleCode: group.module?.moduleCode,
          }),
        },
        { label: "Maximale groepsgrootte", value: group.capacity },
        {
          label: "Docenten",
          value: getTeacherNames(group.teachers),
        },
        { label: "Route", value: projectRoutesMap[group.route] },
        ...(group.legacyMtt1Id
          ? [
              {
                label: "Migratiegroep-ID",
                value: <span className="break-all">{group.legacyMtt1Id}</span>,
              },
            ]
          : []),
      ]}
    />
  )
}
